import { useRef, useState, type ChangeEvent } from "react";
import { ImageIcon, Pencil, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import type { AdminOrganizerDetail } from "@ticketnjoy/types";

import { Button } from "@/components/ui/button";
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  useIsSuperAdmin,
  useOrganizerLogoMutation,
  useUpdateCompanyNameMutation,
  useUpdateContactMutation,
  useUpdateLegalInfoMutation,
} from "@/features/api";
import { showApiError } from "@/lib/api-client";
import { dateTime } from "@/lib/format";

const LOCALE_LABELS: Record<string, string> = { tr: "Türkçe", en: "İngilizce", ar: "Arapça" };
const LOGO_TYPES = ["image/jpeg", "image/png", "image/webp"];
const LOGO_MAX_BYTES = 5 * 1024 * 1024;
const PHONE_PATTERN = /^\+[1-9]\d{7,14}$/;
const TAX_NUMBER_PATTERN = /^\d{10,11}$/;

/** Telefon, dil ve logo Support'a da açık; firma adı süper admin'e özel. Kimseye e-posta gitmez. */
export function OrganizerProfileCard({ organizer }: { organizer: AdminOrganizerDetail }) {
  const isSuper = useIsSuperAdmin();
  const [dialog, setDialog] = useState<"contact" | "name" | null>(null);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profil</CardTitle>
        <CardDescription>Değişiklik organizatörün Aktivite'sine TicketNjoy adıyla düşer.</CardDescription>
        <CardAction>
          <Button size="sm" variant="outline" onClick={() => setDialog("contact")}>
            <Pencil className="size-3.5" />
            Düzenle
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <LogoRow organizer={organizer} />
        <div className="flex items-start justify-between gap-2">
          <InfoRow label="Firma adı" value={organizer.companyName} />
          {isSuper && (
            <Button size="sm" variant="ghost" onClick={() => setDialog("name")} aria-label="Firma adını değiştir">
              <Pencil className="size-3.5" />
            </Button>
          )}
        </div>
        <InfoRow label="E-posta" value={organizer.email} hint="Kullanıcılar kartından sahibin e-postası değiştirilir." />
        <InfoRow label="E-posta doğrulama" value={dateTime(organizer.emailVerifiedAt)} />
        <InfoRow label="Telefon" value={organizer.phoneNumber} />
        <InfoRow
          label="Dil"
          value={LOCALE_LABELS[organizer.preferredLocale] ?? organizer.preferredLocale}
          hint="Yeni mağazaların varsayılan dili."
        />
      </CardContent>

      {dialog === "contact" && <ContactDialog organizer={organizer} onClose={() => setDialog(null)} />}
      {dialog === "name" && <CompanyNameDialog organizer={organizer} onClose={() => setDialog(null)} />}
    </Card>
  );
}

function LogoRow({ organizer }: { organizer: AdminOrganizerDetail }) {
  const logo = useOrganizerLogoMutation(organizer.id);
  const inputRef = useRef<HTMLInputElement>(null);

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!LOGO_TYPES.includes(file.type)) {
      toast.error("Sadece JPEG, PNG ve WebP yüklenebilir.");
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      toast.error("Dosya en fazla 5 MB olabilir.");
      return;
    }
    try {
      await logo.mutateAsync(file);
      toast.success("Logo güncellendi.");
    } catch (err) {
      showApiError(err);
    }
  }

  async function onRemove() {
    try {
      await logo.mutateAsync(null);
      toast.success("Logo kaldırıldı.");
    } catch (err) {
      showApiError(err);
    }
  }

  return (
    <div className="flex items-center gap-3">
      <div className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted/40">
        {organizer.logoUrl ? (
          <img src={organizer.logoUrl} alt="" className="size-full object-contain" />
        ) : (
          <ImageIcon className="size-5 text-muted-foreground" />
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <input
          ref={inputRef}
          type="file"
          accept={LOGO_TYPES.join(",")}
          className="hidden"
          onChange={onFile}
        />
        <Button size="sm" variant="outline" disabled={logo.isPending} onClick={() => inputRef.current?.click()}>
          <Upload className="size-3.5" />
          {logo.isPending ? "Yükleniyor..." : organizer.logoUrl ? "Değiştir" : "Logo yükle"}
        </Button>
        {organizer.logoUrl && (
          <Button size="sm" variant="ghost" disabled={logo.isPending} onClick={onRemove}>
            <Trash2 className="size-3.5" />
            Kaldır
          </Button>
        )}
      </div>
    </div>
  );
}

function ContactDialog({ organizer, onClose }: { organizer: AdminOrganizerDetail; onClose: () => void }) {
  const update = useUpdateContactMutation(organizer.id);
  const [phone, setPhone] = useState(organizer.phoneNumber);
  const [locale, setLocale] = useState(organizer.preferredLocale);
  const normalizedPhone = phone.replace(/[\s()-]/g, "");
  const validPhone = PHONE_PATTERN.test(normalizedPhone);
  const dirty = normalizedPhone !== organizer.phoneNumber || locale !== organizer.preferredLocale;

  async function onSubmit() {
    if (!validPhone || !dirty) return;
    try {
      await update.mutateAsync({ phoneNumber: normalizedPhone, preferredLocale: locale });
      toast.success("Profil güncellendi.");
      onClose();
    } catch (err) {
      showApiError(err);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} disableBackdropClose>
      <DialogContent>
        <DialogHeader title="Telefon ve dil" description="Organizatöre bildirim gitmez." />
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="pf-phone">Telefon</Label>
            <Input
              id="pf-phone"
              inputMode="tel"
              placeholder="+905551234567"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            {!validPhone && (
              <p className="text-xs text-destructive">Ülke koduyla girin, ör. +905551234567.</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="pf-locale">Dil</Label>
            <Select value={locale} onValueChange={setLocale}>
              <SelectTrigger id="pf-locale">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(LOCALE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Yeni mağazaların varsayılan dili; mevcut mağazalar değişmez.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Vazgeç
          </Button>
          <Button onClick={onSubmit} disabled={!validPhone || !dirty || update.isPending}>
            {update.isPending ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompanyNameDialog({ organizer, onClose }: { organizer: AdminOrganizerDetail; onClose: () => void }) {
  const update = useUpdateCompanyNameMutation(organizer.id);
  const [name, setName] = useState(organizer.companyName);
  const trimmed = name.trim();
  const valid = trimmed.length >= 2 && trimmed.length <= 200;

  async function onSubmit() {
    if (!valid || trimmed === organizer.companyName) return;
    try {
      await update.mutateAsync({ companyName: trimmed });
      toast.success("Firma adı güncellendi.");
      onClose();
    } catch (err) {
      showApiError(err);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} disableBackdropClose>
      <DialogContent>
        <DialogHeader
          title="Firma adı"
          description="Panelde ve admin listelerinde görünen ad. Fatura ünvanı ayrı, Fatura bilgileri kartından değişir."
        />
        <div className="space-y-2">
          <Label htmlFor="pf-name">Firma adı</Label>
          <Input id="pf-name" maxLength={200} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            onClick={onSubmit}
            disabled={!valid || trimmed === organizer.companyName || update.isPending}
          >
            {update.isPending ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Organizatör panelindeki yasal bilgi formuyla aynı kurallar: dört alan birlikte, hepsi zorunlu. */
export function OrganizerLegalInfoCard({ organizer }: { organizer: AdminOrganizerDetail }) {
  const isSuper = useIsSuperAdmin();
  const [open, setOpen] = useState(false);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Fatura bilgileri</CardTitle>
        {isSuper && (
          <CardAction>
            <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
              <Pencil className="size-3.5" />
              Düzenle
            </Button>
          </CardAction>
        )}
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <InfoRow label="Ünvan" value={organizer.legalName} />
        <InfoRow label="Vergi no" value={organizer.taxNumber} />
        <InfoRow label="Vergi dairesi" value={organizer.taxOffice} />
        <InfoRow label="Adres" value={organizer.billingAddress} />
      </CardContent>
      {open && <LegalInfoDialog organizer={organizer} onClose={() => setOpen(false)} />}
    </Card>
  );
}

function LegalInfoDialog({ organizer, onClose }: { organizer: AdminOrganizerDetail; onClose: () => void }) {
  const update = useUpdateLegalInfoMutation(organizer.id);
  const [legalName, setLegalName] = useState(organizer.legalName ?? "");
  const [taxNumber, setTaxNumber] = useState(organizer.taxNumber ?? "");
  const [taxOffice, setTaxOffice] = useState(organizer.taxOffice ?? "");
  const [address, setAddress] = useState(organizer.billingAddress ?? "");

  const values = {
    legalName: legalName.trim(),
    taxNumber: taxNumber.trim(),
    taxOffice: taxOffice.trim(),
    billingAddress: address.trim(),
  };
  const valid =
    values.legalName.length >= 2 &&
    TAX_NUMBER_PATTERN.test(values.taxNumber) &&
    values.taxOffice.length >= 2 &&
    values.billingAddress.length >= 10;
  const dirty =
    values.legalName !== (organizer.legalName ?? "") ||
    values.taxNumber !== (organizer.taxNumber ?? "") ||
    values.taxOffice !== (organizer.taxOffice ?? "") ||
    values.billingAddress !== (organizer.billingAddress ?? "");

  async function onSubmit() {
    if (!valid || !dirty) return;
    try {
      await update.mutateAsync(values);
      toast.success("Fatura bilgileri güncellendi.");
      onClose();
    } catch (err) {
      showApiError(err);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} disableBackdropClose>
      <DialogContent>
        <DialogHeader
          title="Fatura bilgileri"
          description="Mağazanın yasal metinlerinde satıcı bilgisi olarak hemen görünür. Organizatöre bildirim gitmez."
        />
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="lg-name">Ünvan</Label>
            <Input id="lg-name" maxLength={200} value={legalName} onChange={(e) => setLegalName(e.target.value)} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="lg-tax">Vergi no / TCKN</Label>
              <Input
                id="lg-tax"
                inputMode="numeric"
                maxLength={11}
                value={taxNumber}
                onChange={(e) => setTaxNumber(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lg-office">Vergi dairesi</Label>
              <Input id="lg-office" maxLength={100} value={taxOffice} onChange={(e) => setTaxOffice(e.target.value)} />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="lg-address">Fatura adresi</Label>
            <Textarea id="lg-address" rows={3} maxLength={500} value={address} onChange={(e) => setAddress(e.target.value)} />
          </div>
          {!valid && (
            <p className="text-xs text-destructive">
              Tüm alanlar zorunlu: vergi no 10 (VKN) ya da 11 (TCKN) hane, adres en az 10 karakter.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Vazgeç
          </Button>
          <Button onClick={onSubmit} disabled={!valid || !dirty || update.isPending}>
            {update.isPending ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InfoRow({ label, value, hint }: { label: string; value: string | null; hint?: string }) {
  return (
    <div className="min-w-0">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="break-words">{value || "—"}</div>
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}
